import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, P } from "@/components/studio/portalKit";

export const GLASS = ["Windscreen", "Rear glass", "Front doors", "Rear doors", "Quarter glass", "Sunroof"];

export const Section = ({ title, children }) => (
  <section className="space-y-4 rounded-xl border border-brand-line bg-white p-5">
    <h3 className="text-sm font-bold uppercase tracking-wider text-brand-deep">{title}</h3>
    {children}
  </section>
);

export const CustomerFields = ({ f, set }) => (
  <Section title="Customer">
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Name *"><Input data-testid="cust-name" value={f.customer.name} onChange={(e) => set("customer", "name", e.target.value)} /></Field>
      <Field label="Mobile *"><Input data-testid="cust-mobile" inputMode="tel" value={f.customer.mobile} onChange={(e) => set("customer", "mobile", e.target.value)} /></Field>
      <Field label="Email *"><Input data-testid="cust-email" type="email" value={f.customer.email} onChange={(e) => set("customer", "email", e.target.value)} /></Field>
      <Field label="Address"><Input data-testid="cust-address" value={f.customer.address} onChange={(e) => set("customer", "address", e.target.value)} /></Field>
    </div>
  </Section>
);

export const VehicleFields = ({ f, set, editingId }) => {
  const [dup, setDup] = useState(false);
  const check = () => {
    if (!f.vehicle.reg_no.trim() || editingId) return setDup(false);
    api.get(`${P}/check-reg`, { params: { reg_no: f.vehicle.reg_no } }).then(({ data }) => setDup(data.exists));
  };
  return (
    <Section title="Vehicle">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Make *"><Input data-testid="veh-make" value={f.vehicle.make} onChange={(e) => set("vehicle", "make", e.target.value)} /></Field>
        <Field label="Model *"><Input data-testid="veh-model" value={f.vehicle.model} onChange={(e) => set("vehicle", "model", e.target.value)} /></Field>
        <Field label="Registration no. *"><Input data-testid="veh-reg" value={f.vehicle.reg_no} onBlur={check} onChange={(e) => set("vehicle", "reg_no", e.target.value.toUpperCase())} /></Field>
        <Field label="Colour"><Input data-testid="veh-colour" value={f.vehicle.colour} onChange={(e) => set("vehicle", "colour", e.target.value)} /></Field>
        <Field label="Chassis no. / VIN *"><Input data-testid="veh-vin" value={f.vehicle.vin} onChange={(e) => set("vehicle", "vin", e.target.value.toUpperCase())} /></Field>
      </div>
      {dup && (
        <p data-testid="dup-reg-warning" className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" /> This registration number already has a warranty on record.
        </p>
      )}
    </Section>
  );
};

export const SerialPicker = ({ brand, product, value, onChange, required, credit, testid = "serial-select" }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!brand) return;
    api.get(`${P}/serials`, { params: { brand, product } }).then(({ data }) => setRows(data));
  }, [brand, product]);
  const opts = rows.map((r) => (credit?.id === r.id ? { ...r, balance_sqft: r.balance_sqft + Number(credit.sqft || 0) } : r));
  return (
    <Field label={`Roll serial${required ? " *" : ""}`}>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={rows.length ? "Choose your roll" : "No rolls with sqft left"} /></SelectTrigger>
        <SelectContent>
          {opts.map((r) => (
            <SelectItem key={r.id} value={r.id} data-testid={`serial-opt-${r.serial}`}>
              {r.serial} · {r.product_name} · {r.balance_sqft} sqft left
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
};
